import {
  Check,
  Eraser,
  Loader,
  Maximize2,
  MessageSquarePlus,
  Minimize2,
  Send,
  Sparkles,
  Undo2,
  User,
  X,
} from 'lucide-react'
import React, {FC, FormEvent, useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'

import type {
  AiAllocationAction,
  AiAllocationContext,
  AiAllocationResponse,
  AiDisambiguationOption,
  AllocationScope,
  ConversationTurn,
} from '../types/teacherAllocationAi'
import {parseAllocationSlotKey} from '../utils/allocationIds'
import {buildCurriculumPrefixIndex, displayCurriculumName} from '../utils/curriculumDisplay'
import {buildSharedNameLabeler} from '../utils/levelCurriculum'
import {getEffectiveAllocationActions} from '../utils/normalizeAllocationAiResponse'

type Props = {
  isDark: boolean
  isLoading: boolean
  isApplying: boolean
  disabled: boolean
  scope: AllocationScope
  onScopeChange: (scope: AllocationScope) => void
  hasMultipleBranches: boolean
  onAsk: (prompt: string) => void
  hasPreview: boolean
  conversationHistory: ConversationTurn[]
  preview: AiAllocationResponse | null
  lastContext: AiAllocationContext | null
  onApply: () => void
  onCancel: () => void
  onResolveDisambiguation: (option: AiDisambiguationOption) => void
  onClearAll: () => void
  onRevert: () => void
  onStartNewChat: () => void
  canClearAll: boolean
  canRevert: boolean
  canStartNewChat: boolean
  // Bumped by the parent to force the chat window closed (e.g. when advancing to
  // the next wizard step) — collapses the widget back to the floating button.
  closeSignal?: number
  /** Reports every open/close of the chat widget (fab click, close button, closeSignal). */
  onOpenStateChange?: (open: boolean) => void
  /**
   * Mount with the chat window already open instead of collapsed to the floating button.
   * The lesson-planning step wants the assistant in front of the user the moment they land,
   * not hidden behind a fab they may never notice. Read once, on mount.
   */
  defaultOpen?: boolean
  /**
   * Display-only rewrite of free text (chat turns, warnings) — used to hide the curriculum
   * prefix of class/level names. The conversation sent to the API is never changed.
   */
  formatText?: (text: string) => string
}

const TeacherAllocationAiPanel: FC<Props> = ({
  isDark,
  isLoading,
  isApplying,
  disabled,
  scope,
  onScopeChange,
  hasMultipleBranches,
  onAsk,
  hasPreview,
  conversationHistory,
  preview,
  lastContext,
  onApply,
  onCancel,
  onResolveDisambiguation,
  onClearAll,
  onRevert,
  onStartNewChat,
  canClearAll,
  canRevert,
  canStartNewChat,
  closeSignal,
  onOpenStateChange,
  defaultOpen = false,
  formatText,
}) => {
  const intl = useIntl()
  const fmt = (text: string) => (formatText ? formatText(text) : text)
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [isMaximized, setIsMaximized] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [showAllActions, setShowAllActions] = useState(false)
  const [expandedTurns, setExpandedTurns] = useState<Set<number>>(new Set())
  const [showInputBlockedHint, setShowInputBlockedHint] = useState(false)
  const prevHasPreviewRef = useRef(hasPreview)
  const prevCloseSignalRef = useRef(closeSignal)
  const messagesRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Collapse the chat window whenever the parent bumps closeSignal (e.g. the user
  // pressed Next on the lesson-planning step while the chatbot was open).
  useEffect(() => {
    if (closeSignal === prevCloseSignalRef.current) return
    prevCloseSignalRef.current = closeSignal
    setIsOpen(false)
  }, [closeSignal])

  // Report open/close transitions to the parent (analytics), whichever path
  // caused them — fab click, close button, or a closeSignal bump.
  const prevIsOpenRef = useRef(isOpen)
  useEffect(() => {
    if (prevIsOpenRef.current === isOpen) return
    prevIsOpenRef.current = isOpen
    onOpenStateChange?.(isOpen)
  }, [isOpen, onOpenStateChange])

  useEffect(() => {
    if (prevHasPreviewRef.current && !hasPreview) {
      setPrompt('')
    }
    prevHasPreviewRef.current = hasPreview
  }, [hasPreview])

  useEffect(() => {
    if (!hasPreview) {
      setShowInputBlockedHint(false)
    }
  }, [hasPreview])

  // Every new proposal starts collapsed again.
  useEffect(() => {
    setShowAllActions(false)
  }, [preview])

  // A cleared conversation (new chat) drops the per-message expand state —
  // indices would otherwise leak onto the next conversation's messages.
  useEffect(() => {
    if (conversationHistory.length === 0) setExpandedTurns(new Set())
  }, [conversationHistory.length])

  useEffect(() => {
    if (!isOpen) return
    const el = messagesRef.current
    if (!el) return
    const scrollToLatest = () => {
      el.scrollTop = el.scrollHeight
    }
    scrollToLatest()
    const raf = requestAnimationFrame(scrollToLatest)
    return () => cancelAnimationFrame(raf)
  }, [conversationHistory, preview, isLoading, isOpen])

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
    }
  }, [isOpen])

  const slotMeta = useMemo(() => {
    const map = new Map<string, {className: string; subjectName: string}>()
    lastContext?.subjects.forEach((s) => {
      map.set(s.slotKey, {className: s.className, subjectName: s.name})
    })
    return map
  }, [lastContext])

  // Display-only: class names without the curriculum prefix (the context keeps full names).
  const prefixIndex = useMemo(
    () => buildCurriculumPrefixIndex(lastContext?.classes ?? []),
    [lastContext]
  )
  // A class name two curricula share is shown with its short code: "Grade 1-A (FBISE)".
  const sharedNameLabel = useMemo(() => {
    const shortCodes: Record<string, string> = {}
    lastContext?.classes.forEach((c) => {
      if (c.curriculum && c.curriculum_short_code)
        shortCodes[c.curriculum] = c.curriculum_short_code
    })
    return buildSharedNameLabeler(lastContext?.classes ?? [], shortCodes)
  }, [lastContext])

  const describeSlot = (slotKey: string) => {
    const {classKey, subjectName} = parseAllocationSlotKey(slotKey)
    const parts = classKey.split('|||')
    const curriculum = parts.length >= 3 ? parts[1] : undefined
    const meta = slotMeta.get(slotKey)
    const className = meta?.className ?? (parts[parts.length - 1] || classKey)
    return {
      className: sharedNameLabel(
        displayCurriculumName(prefixIndex, className, curriculum),
        className,
        curriculum,
        parts.length >= 2 ? parts[0] : undefined
      ),
      subjectName: meta?.subjectName ?? subjectName,
    }
  }

  const describeTeacher = (action: AiAllocationAction) => {
    if (action.type === 'unassign') return '—'
    const byName = String(action.teacherName ?? '').trim()
    if (byName) return byName
    const employeeId = String(action.teacherEmployeeId ?? '').trim()
    if (employeeId && lastContext) {
      const teacher = lastContext.teachers.find((t) => t.employeeId === employeeId)
      if (teacher) return teacher.name
    }
    return employeeId || '—'
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!prompt.trim() || disabled || isLoading || hasPreview) return
    onAsk(prompt.trim())
    setPrompt('')
  }

  const allBranchesActive = scope === 'all_branches'
  const disambiguation = preview?.disambiguation
  const isPreviewBusy = isApplying || isLoading
  const effectiveActions = preview ? getEffectiveAllocationActions(preview) : []
  const previewActionCount = effectiveActions.length
  const MAX_VISIBLE_ACTIONS = 10
  const visibleActions = showAllActions
    ? effectiveActions
    : effectiveActions.slice(0, MAX_VISIBLE_ACTIONS)
  const hiddenActionCount = previewActionCount - visibleActions.length

  // Long assistant replies collapse to this many characters with a
  // "Show more" toggle, mirroring the proposed-changes list above.
  const MAX_MSG_CHARS = 280
  const toggleTurnExpanded = (idx: number) => {
    setExpandedTurns((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const title = intl.formatMessage({
    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.TITLE',
  })

  /* Keep the chat/panel (maximized) mode across close/reopen so the widget
     reopens in whichever mode it was closed in. */
  const closeWidget = () => {
    setIsOpen(false)
  }

  const fab = (
    <button
      type='button'
      className={`step4-ai-fab${isDark ? ' step4-ai-fab--dark' : ''}`}
      onClick={() => setIsOpen(true)}
      aria-label={intl.formatMessage({
        id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_OPEN',
      })}
    >
      <Sparkles size={26} strokeWidth={2} />
      {(hasPreview || isLoading) && <span className='step4-ai-fab-dot' />}
      <span className='step4-ai-fab-label' aria-hidden='true'>
        {intl.formatMessage({
          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.FAB_LABEL',
        })}
      </span>
    </button>
  )

  const widget = (
    <div
      className={`step4-ai-widget${isDark ? ' step4-ai-widget--dark' : ''}${
        isMaximized ? ' step4-ai-widget--maximized' : ''
      }`}
      role='dialog'
      aria-label={title}
    >
      <div className='step4-ai-widget-header'>
        <div className='step4-ai-widget-avatar'>
          <Sparkles size={20} strokeWidth={2.1} />
        </div>
        <div className='step4-ai-widget-heading'>
          <span className='step4-ai-widget-title'>{title}</span>
          <span className='step4-ai-widget-status'>
            <span className='step4-ai-widget-status-dot' />
            {intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.STATUS_ONLINE',
            })}
            {' · '}
            {intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.TAGLINE',
            })}
          </span>
        </div>
        <div className='step4-ai-widget-header-actions'>
          <button
            type='button'
            className='step4-ai-widget-header-btn'
            onClick={() => setIsMaximized((v) => !v)}
            title={intl.formatMessage({
              id: isMaximized
                ? 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_RESTORE'
                : 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_MAXIMIZE',
            })}
            aria-label={intl.formatMessage({
              id: isMaximized
                ? 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_RESTORE'
                : 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_MAXIMIZE',
            })}
          >
            {isMaximized ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
          <button
            type='button'
            className='step4-ai-widget-header-btn step4-ai-widget-header-btn--close'
            onClick={closeWidget}
            title={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_CLOSE',
            })}
            aria-label={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_CLOSE',
            })}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div className='step4-ai-widget-scopebar'>
        <label
          className='step4-ai-widget-scope'
          title={
            !hasMultipleBranches
              ? intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.SCOPE_SINGLE_BRANCH',
                })
              : undefined
          }
        >
          <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid step4-ai-widget-scope-switch'>
            <input
              className='form-check-input'
              type='checkbox'
              role='switch'
              checked={allBranchesActive}
              disabled={disabled || isLoading || hasPreview || !hasMultipleBranches}
              aria-label={intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.CHIP_ALL_BRANCHES',
              })}
              onChange={(e) => onScopeChange(e.target.checked ? 'all_branches' : 'active_filters')}
            />
          </div>
          <span className='step4-ai-widget-scope-label'>
            {intl.formatMessage({
              id: allBranchesActive
                ? 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.SCOPE_ALL'
                : 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.SCOPE_FILTERED',
            })}
          </span>
        </label>
        <div className='step4-ai-widget-tools'>
          <button
            type='button'
            className='step4-ai-widget-tool-btn step4-ai-widget-tool-btn--danger'
            disabled={!canClearAll}
            onClick={onClearAll}
            title={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_CLEAR_ALL',
            })}
            aria-label={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_CLEAR_ALL',
            })}
          >
            <Eraser size={15} strokeWidth={2.1} />
          </button>
          <button
            type='button'
            className='step4-ai-widget-tool-btn'
            disabled={!canRevert}
            onClick={onRevert}
            title={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_REVERT',
            })}
            aria-label={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_REVERT',
            })}
          >
            <Undo2 size={15} strokeWidth={2.1} />
          </button>
          <button
            type='button'
            className='step4-ai-widget-tool-btn'
            disabled={!canStartNewChat}
            onClick={onStartNewChat}
            title={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_NEW_CHAT_HINT',
            })}
            aria-label={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_NEW_CHAT',
            })}
          >
            <MessageSquarePlus size={15} strokeWidth={2.1} />
          </button>
        </div>
      </div>

      <div className='step4-ai-widget-messages' ref={messagesRef}>
        <div className='step4-ai-widget-row step4-ai-widget-row--assistant'>
          <div className='step4-ai-widget-msg-avatar'>
            <Sparkles size={14} strokeWidth={2.2} />
          </div>
          <div className='step4-ai-widget-bubble step4-ai-widget-bubble--assistant'>
            {intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.WELCOME',
            })}
          </div>
        </div>

        {conversationHistory.map((turn, idx) =>
          turn.role === 'user' ? (
            <div key={`user-${idx}`} className='step4-ai-widget-row step4-ai-widget-row--user'>
              <div className='step4-ai-widget-bubble step4-ai-widget-bubble--user'>
                {fmt(turn.content)}
              </div>
              <div className='step4-ai-widget-msg-avatar step4-ai-widget-msg-avatar--user'>
                <User size={14} strokeWidth={2.2} />
              </div>
            </div>
          ) : (
            <div
              key={`assistant-${idx}`}
              className='step4-ai-widget-row step4-ai-widget-row--assistant'
            >
              <div className='step4-ai-widget-msg-avatar'>
                <Sparkles size={14} strokeWidth={2.2} />
              </div>
              <div className='step4-ai-widget-bubble step4-ai-widget-bubble--assistant'>
                {fmt(turn.content).length > MAX_MSG_CHARS && !expandedTurns.has(idx)
                  ? `${fmt(turn.content).slice(0, MAX_MSG_CHARS).trimEnd()}…`
                  : fmt(turn.content)}
                {fmt(turn.content).length > MAX_MSG_CHARS && (
                  <button
                    type='button'
                    className='step4-ai-widget-msg-toggle'
                    onClick={() => toggleTurnExpanded(idx)}
                  >
                    {intl.formatMessage({
                      id: expandedTurns.has(idx)
                        ? 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_SHOW_LESS'
                        : 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_SHOW_MORE_TEXT',
                    })}
                  </button>
                )}
              </div>
            </div>
          )
        )}

        {preview && (
          <div className='step4-ai-widget-card'>
            <div className='step4-ai-widget-card-title'>
              {disambiguation
                ? intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.DISAMBIGUATION_TITLE',
                  })
                : `${intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.PROPOSED_CHANGES',
                  })} · ${previewActionCount}`}
            </div>

            {disambiguation ? (
              <div className='d-flex flex-wrap gap-2 mt-1'>
                {disambiguation.options.map((option) => (
                  <button
                    key={option.employeeId}
                    type='button'
                    className='step4-ai-widget-chip'
                    disabled={isPreviewBusy}
                    onClick={() => onResolveDisambiguation(option)}
                  >
                    {option.displayName}
                  </button>
                ))}
              </div>
            ) : (
              <div className='step4-ai-widget-card-rows'>
                {visibleActions.map((action, idx) => {
                  const {className, subjectName} = describeSlot(action.slotKey)
                  const isUnassign = action.type === 'unassign'
                  return (
                    <div key={`${action.slotKey}-${idx}`} className='step4-ai-widget-card-row'>
                      <span className='step4-ai-widget-card-class' title={className}>
                        {className}
                      </span>
                      <span className='step4-ai-widget-card-change'>
                        {subjectName}
                        <span className='step4-ai-widget-card-arrow'>→</span>
                        <strong>{describeTeacher(action)}</strong>
                      </span>
                      <span
                        className={`step4-ai-widget-card-badge${
                          isUnassign ? ' step4-ai-widget-card-badge--danger' : ''
                        }`}
                      >
                        {intl.formatMessage({
                          id: isUnassign
                            ? 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BADGE_UNASSIGN'
                            : 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BADGE_TEACHER',
                        })}
                      </span>
                    </div>
                  )
                })}
                {previewActionCount > MAX_VISIBLE_ACTIONS && (
                  <button
                    type='button'
                    className='step4-ai-widget-showmore'
                    onClick={() => setShowAllActions((v) => !v)}
                  >
                    {showAllActions
                      ? intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_SHOW_LESS',
                        })
                      : intl.formatMessage(
                          {
                            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_SHOW_MORE',
                          },
                          {count: hiddenActionCount}
                        )}
                  </button>
                )}
              </div>
            )}

            {preview.warnings.length > 0 && (
              <div className='step4-ai-widget-warnings'>
                <div className='step4-ai-widget-warnings-title'>
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.WARNINGS_TITLE',
                  })}
                </div>
                <ul className='mb-0 ps-3'>
                  {preview.warnings.map((warning, idx) => (
                    <li key={`${warning}-${idx}`}>{fmt(warning)}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {isLoading && (
          <div className='step4-ai-widget-row step4-ai-widget-row--assistant'>
            <div className='step4-ai-widget-msg-avatar'>
              <Sparkles size={14} strokeWidth={2.2} />
            </div>
            <div className='step4-ai-widget-bubble step4-ai-widget-bubble--assistant step4-ai-widget-typing'>
              <Loader size={14} className='step4-ai-widget-spin' />
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.LOADING',
              })}
            </div>
          </div>
        )}
      </div>

      {preview && (
        <div className='step4-ai-widget-approvebar'>
          {!disambiguation && (
            <button
              type='button'
              className='step4-ai-widget-btn step4-ai-widget-btn--approve'
              onClick={onApply}
              disabled={isApplying || previewActionCount === 0}
            >
              {isApplying ? (
                <>
                  <Loader size={15} className='step4-ai-widget-spin' />
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.APPLYING',
                  })}
                </>
              ) : (
                <>
                  <Check size={15} strokeWidth={2.4} />
                  {intl.formatMessage(
                    {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_APPROVE'},
                    {count: previewActionCount}
                  )}
                </>
              )}
            </button>
          )}
          <button
            type='button'
            className='step4-ai-widget-btn step4-ai-widget-btn--discard'
            onClick={onCancel}
            disabled={isApplying}
          >
            {intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_DISCARD',
            })}
          </button>
        </div>
      )}

      <form className='step4-ai-widget-composer' onSubmit={handleSubmit}>
        <div
          className='step4-ai-widget-input-wrap'
          onMouseEnter={() => {
            if (hasPreview) setShowInputBlockedHint(true)
          }}
          onMouseLeave={() => setShowInputBlockedHint(false)}
        >
          {showInputBlockedHint && hasPreview && (
            <div className='step4-ai-widget-input-tooltip' role='tooltip'>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.INPUT_BLOCKED_PREVIEW',
              })}
            </div>
          )}
          <Sparkles size={16} className='step4-ai-widget-input-icon' />
          <input
            ref={inputRef}
            type='text'
            className='step4-ai-widget-input'
            placeholder={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.INPUT_PLACEHOLDER',
            })}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={disabled || isLoading || hasPreview}
          />
          <button
            type='submit'
            className='step4-ai-widget-send'
            disabled={disabled || isLoading || hasPreview || !prompt.trim()}
            title={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_ASK',
            })}
            aria-label={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.AI.BTN_ASK',
            })}
          >
            {isLoading ? (
              <Loader size={16} className='step4-ai-widget-spin' />
            ) : (
              <Send size={16} strokeWidth={2.2} />
            )}
          </button>
        </div>
      </form>
    </div>
  )

  return createPortal(isOpen ? widget : fab, document.body)
}

export default TeacherAllocationAiPanel
